import React, { Fragment } from "react";
import styled from "styled-components";
import PublishRide from "../../components/Publish";
import { useDispatch, useSelector } from "react-redux";
import * as types from "../../redux/constants";
import api from "../../services/api";
import Stepper from "../../components/Stepper";
import VehicleInfo from "../VehicleInfo";
import { useState } from "react";
import RidePublishSuccess from "../publishSuccess";
import RideSuccess from "../../components/RideSuccess";
import moment from "moment";
import { useEffect } from "react";
import { toast } from "react-toastify";
import VerifyDrivingLicense from "../../components/DrivingLicense";
import noData from "../../assets/images/noData.svg";
import { Message, Title } from "../../components/NoData/styles";
import CircularLoader from "../../components/UI/Loader";
import { COLORS } from "../../assets/colors";

const steps = ["Create a Ride", "Add Car Details", "Publish"];

function PublishRides() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [skipped, setSkipped] = React.useState(new Set());
  const [licenseStatus, setLicenseStatus] = React.useState(null);
  const [checkingLicense, setCheckingLicense] = React.useState(false);
  const [rideId, setRideId] = useState();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const { isLoading } = useSelector((state) => state.publish);
  const { currentUser } = useSelector((state) => state.auth);
  useEffect(() => {
    const activeStep = localStorage.getItem("activeStep");
    const currentRideId = localStorage.getItem("rideId");
    if (activeStep && currentRideId) {
      setActiveStep(parseInt(activeStep));
      setRideId(currentRideId);
    }
    // check license
    checkLicense();
  }, []);

  const checkLicense = async () => {
    try {
      setCheckingLicense(true);
      const response = await api.get("/user/checkLicense");
      if (response.status == 200) {
        setLicenseStatus(response.data.data.license_status);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setCheckingLicense(false);
    }
  };
  const ridesSubmitFrom = async (data) => {
    setSubmitting(true);
    // add to gtm
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "submit_ride", // Custom event name
      category: "Button", // Category of the event
      action: "Click", // Action (what user did)
      label: "Submit ride form", // Label to identify the element
      userId: currentUser.id,
      userName: currentUser.first_name,
      userEmail: currentUser.email,
    });
    dispatch({
      type: types.PUBLISH_RIDE_REQUEST,
    });
    if (!data.headingTo || !data.leavingFrom) {
      toast.error("Please enter valid info");
      return;
    }

    await api
      .post("/ride/create", {
        leaving_from: data.leavingFrom,
        heading_to: data.headingTo,
        route: data.route,
        departure_date: moment(data.date).format("YYYY-MM-DD"),
        departure_time: data.time,
        user_id: currentUser.id,
        ticket: data.ticket,
        pickup_location: data.pickupLocation,
        drop_location: data.dropLocation,
        arrival_date: moment(data.arrivalDate).format("YYYY-MM-DD"),
        arrival_time: data.arrivalTime,
        total_seats: data.totalSeats,
        start_lat: data.start_lat,
        start_lng: data.start_lng,
        end_lat: data.end_lat,
        end_lng: data.end_lng,
        duration: data.duration,
        distance: data.distance,
        smoking: data.smoking,
        automatic_approval: data.automatic_approval,
        female_only: data.female_only,
        front_seat_price: data.front_seat_price || null,
        back_seat_price: data.back_seat_price || null,
        front_seats_total: data.front_seats_total || null,
        back_seats_total: data.back_seats_total || null,
        ride_wish_id: data.wishId || null,
      })
      .then((result) => {
        setSubmitting(false);
        window.dataLayer.push({
          event: "submit_ride_success", // Custom event name
          category: "Button", // Category of the event
          action: "Click", // Action (what user did)
          label: "Submit ride form_success", // Label to identify the element
          userId: currentUser.id,
          userName: currentUser.first_name,
          userEmail: currentUser.email,
        });

        setRideId(result.data.data.id);
        handleNext(result.data.data.id);
      })
      .catch((error) => {
        setSubmitting(false);
        window.dataLayer.push({
          event: "submit_ride_failed", // Custom event name
          category: "Button", // Category of the event
          action: "Click", // Action (what user did)
          label: "Submit ride failed", // Label to identify the element
          userId: currentUser.id,
          userName: currentUser.first_name,
          userEmail: currentUser.email,
        });
        if (error.response.data?.message) {
          toast.error(error.response.data.message);
        }
        dispatch({
          type: types.PUBLISH_RIDE_FAILED,
          payload: error.response.data,
        });
      });
  };

  const isStepOptional = (step) => {
    return step === 1;
  };

  const isStepSkipped = (step) => {
    return skipped.has(step);
  };

  const handleNext = (currentRideId = null) => {
    let newSkipped = skipped;
    if (isStepSkipped(activeStep)) {
      newSkipped = new Set(newSkipped.values());
      newSkipped.delete(activeStep);
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    localStorage.setItem("activeStep", activeStep + 1);
    if (currentRideId != null) {
      localStorage.setItem("rideId", currentRideId);
    }

    setSkipped(newSkipped);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    if (!isStepOptional(activeStep)) {
      throw new Error("You can't skip a step that isn't optional.");
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSkipped((prevSkipped) => {
      const newSkipped = new Set(prevSkipped.values());
      newSkipped.add(activeStep);
      return newSkipped;
    });
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  const renderStep = (step) => {
    switch (step) {
      case 1:
        return (
          <PublishRide
            ridesSubmitFrom={ridesSubmitFrom}
            isLoading={isLoading}
            currentUser={currentUser}
            submitting={submitting}
            licenseStatus={licenseStatus}
            checkLicense={checkLicense}
          />
        );
      case 2:
        return <VehicleInfo rideId={rideId} handleNext={handleNext} />;
      case 3:
        return (
          <RidePublishSuccess
            ridesSubmitFrom={ridesSubmitFrom}
            isLoading={isLoading}
            rideId={rideId}
            handleNext={handleNext}
          />
        );

      default:
        return null;
    }
  };

  const renderLicenseSection = (status) => {
    if (!status) {
      return (
        <LicenseGateWrapper>
          <div className="welcome-card">
            <div className="welcome-icon">🚗</div>
            <h2>Welcome, Driver!</h2>
            <p>
              Before you can offer rides on GoSawari, we need to verify your
              driving license. This is a one-time process to ensure safety for
              all users.
            </p>
          </div>
          <VerifyDrivingLicense
            checkLicense={checkLicense}
            title="Upload your driving license"
            message="Please upload clear images of the front and back of your license"
          />
        </LicenseGateWrapper>
      );
    } else if (status === "submitted") {
      return (
        <LicenseGateWrapper>
          <div className="status-card submitted">
            <div className="status-icon">⏳</div>
            <Title>License Under Review</Title>
            <Message>
              Thanks for uploading your driving license! Our team will verify it
              within 1 hour. You'll be able to publish rides once it's approved.
            </Message>
          </div>
        </LicenseGateWrapper>
      );
    } else if (status === "rejected") {
      return (
        <LicenseGateWrapper>
          <div className="status-card rejected">
            <div className="status-icon">⚠️</div>
            <Title>License Verification Failed</Title>
            <Message>
              We couldn't verify your license. Please re-upload clear, valid
              images.
            </Message>
          </div>
          <VerifyDrivingLicense
            checkLicense={checkLicense}
            title="Re-upload your driving license"
            message="Please upload clear, valid images of your license"
          />
        </LicenseGateWrapper>
      );
    } else return null;
  };
  if (checkingLicense) {
    return (
      <PageContainer>
        {<CircularLoader isLoading={checkingLicense} />}
      </PageContainer>
    );
  }
  return (
    <PageContainer>
      {renderLicenseSection(licenseStatus)}
      {licenseStatus === "verified" && (
        <Fragment>
          <Stepper
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            skipped={skipped}
            setSkipped={setSkipped}
            isStepOptional={isStepOptional}
            isStepSkipped={isStepSkipped}
            handleNext={handleNext}
            handleBack={handleBack}
            handleSkip={handleSkip}
            handleReset={handleReset}
            steps={steps}
          />
          {renderStep(activeStep + 1)}
          {activeStep === steps.length && <RideSuccess rideId={rideId} />}
        </Fragment>
      )}
    </PageContainer>
  );
}

export default PublishRides;
const PageContainer = styled.div`
  width: 100%;
  height: 100%;
  .license-section {
    margin-top: 3rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: center;
    justify-content: center;
    align-items: center;
  }
`;

const LicenseGateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 1rem;
  gap: 2rem;
  max-width: 600px;
  margin: 0 auto;

  .welcome-card {
    text-align: center;
    padding: 2.5rem 2rem;
    background: linear-gradient(135deg, rgba(5, 71, 82, 0.04) 0%, rgba(5, 71, 82, 0.01) 100%);
    border: 1px solid rgba(5, 71, 82, 0.12);
    border-radius: 20px;
    width: 100%;

    .welcome-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 80px;
      height: 80px;
      background: rgba(5, 71, 82, 0.08);
      border-radius: 50%;
    }

    h2 {
      font-size: 1.6rem;
      font-weight: 700;
      color: ${COLORS.primaryColor};
      margin: 0 0 0.75rem 0;
    }

    p {
      font-size: 1rem;
      color: #64748b;
      line-height: 1.6;
      margin: 0;
    }
  }

  .status-card {
    text-align: center;
    padding: 2.5rem 2rem;
    border-radius: 20px;
    width: 100%;

    .status-icon {
      font-size: 3rem;
      margin-bottom: 1rem;
    }

    &.submitted {
      background: linear-gradient(135deg, rgba(245, 158, 11, 0.06) 0%, rgba(245, 158, 11, 0.02) 100%);
      border: 1px solid rgba(245, 158, 11, 0.2);
    }

    &.rejected {
      background: linear-gradient(135deg, rgba(239, 68, 68, 0.06) 0%, rgba(239, 68, 68, 0.02) 100%);
      border: 1px solid rgba(239, 68, 68, 0.2);
    }
  }
`;
